// @flow
import ApiService from '@api/service';
import EventEmitter from '_common/services/EventEmitter';

import type { Listener, Error } from '@core/types';
import { API_PATHS } from '@app/constants/paths';
import type { InterventionRegistration, InterventionRegistrationType } from '@root/src/Intervention/types';

const { INTERVENTION_REGISTRATIONS, UPDATE_GROUP_CLASS_INTERVENTIONS } = API_PATHS;

export type InterventionRegistrationServiceData = {
  registrations: InterventionRegistration[],
  isLoading: boolean,
  errors: Error[],
};

type FetchAll = (interventionId: number, language: string) => Promise<InterventionRegistration[]>;
type RegistrationStatusUpdate = {|
  beneficiaryId: number | null,
  participantId: number | null,
  status: InterventionRegistrationType,
|};
type UpdateStatuses = (interventionId: number, language: string, registrations: RegistrationStatusUpdate[]) => Promise<InterventionRegistration[]>;
type UpdateValues = (registrations: InterventionRegistration[], isLoading: boolean, errors?: Error[]) => void;
type OnChange = (listener: Listener) => Function;
type Trigger = () => void;

let sourceAll = ApiService.createToken();
let sourceUpdate = ApiService.createToken();

class InterventionRegistrationService {
  constructor() {
    this.eventEmitter = new EventEmitter();
    this.registrations = [];
    this.isLoading = false;
    this.errors = [];
  }

  eventEmitter: EventEmitter;
  registrations: InterventionRegistration[];
  isLoading: boolean;
  errors: Error[];

  get registrationsValue(): InterventionRegistration[] {
    return this.registrations;
  }

  get isLoadingValue(): boolean {
    return this.isLoading;
  }

  get errorsValue(): Error[] {
    return this.errors;
  }

  updateValues: UpdateValues = (
    registrations: InterventionRegistration[],
    isLoading: boolean,
    errors: Error[] = [],
  ): void => {
    this.registrations = registrations;
    this.isLoading = isLoading;
    this.errors = errors;
    this.#trigger();
  };

  normalizeRegistration = (registration: any): InterventionRegistration => {
    const beneficiaryId = registration?.beneficiaryId
      ?? registration?.beneficiary?.id
      ?? null;
    const participantId = registration?.participantId
      ?? registration?.participant?.id
      ?? null;

    return {
      ...registration,
      beneficiaryId,
      participantId,
      personType: registration?.personType
        ?? (beneficiaryId !== null ? 'beneficiary' : (participantId !== null ? 'participant' : null)),
    };
  };

  normalizeRegistrations = (registrations: any[]): InterventionRegistration[] => (
    registrations.map(this.normalizeRegistration)
  );

  fetchAll: FetchAll = (interventionId: number, language: string): Promise<InterventionRegistration[]> => {
    sourceAll.cancel();
    sourceAll = ApiService.createToken();

    this.updateValues(this.registrations, true, this.errors);

    return ApiService.request({
      method: 'get',
      url: INTERVENTION_REGISTRATIONS.replace(':id', String(interventionId)),
      cancelToken: sourceAll.token,
      headers: {
        'Accept-Language': language,
      },
    })
      .then((payload) => {
        if (payload.data) {
          const rawData = Array.isArray(payload.data) ? payload.data : payload.data['hydra:member'] || [];
          const normalized = this.normalizeRegistrations(rawData);
          this.updateValues(normalized, false);
          return normalized;
        }
        this.updateValues(this.registrations, false, this.errors);
        return Promise.reject();
      })
      .catch((error) => {
        this.updateValues(this.registrations, false, [...this.errors, { error }]);
        return Promise.reject();
      });
  };

  updateStatuses: UpdateStatuses = (
    interventionId: number,
    language: string,
    registrations: RegistrationStatusUpdate[],
  ): Promise<InterventionRegistration[]> => {
    sourceUpdate.cancel();
    sourceUpdate = ApiService.createToken();

    this.updateValues(this.registrations, true, []);

    const getRegistrationKey = (registration: RegistrationStatusUpdate | InterventionRegistration): string => {
      if (registration.beneficiaryId !== null && registration.beneficiaryId !== undefined) {
        return `beneficiary-${ registration.beneficiaryId }`;
      }
      if (registration.participantId !== null && registration.participantId !== undefined) {
        return `participant-${ registration.participantId }`;
      }
      return 'unknown';
    };

    return ApiService.request({
      method: 'patch',
      url: UPDATE_GROUP_CLASS_INTERVENTIONS.replace(':id', String(interventionId)),
      cancelToken: sourceUpdate.token,
      headers: {
        'Accept-Language': language,
        'Content-Type': 'application/merge-patch+json',
      },
      data: { registrations },
    })
      .then((payload) => {
        if (payload?.data) {
          const rawData = Array.isArray(payload.data) ? payload.data : payload.data['hydra:member'] || [];
          const normalized = this.normalizeRegistrations(rawData);
          this.updateValues(normalized, false, []);
          return normalized;
        }
        const statusByKey: { [string]: InterventionRegistrationType } = registrations.reduce((acc, registration) => {
          acc[getRegistrationKey(registration)] = registration.status;
          return acc;
        }, ({}: { [string]: InterventionRegistrationType }));

        const updatedRegistrations: InterventionRegistration[] = this.registrations.map((registration) => {
          const updatedStatus = statusByKey[getRegistrationKey(registration)];
          return updatedStatus ? { ...registration, status: updatedStatus } : registration;
        });

        const normalized = this.normalizeRegistrations(updatedRegistrations);
        this.updateValues(normalized, false, []);
        return normalized;
      })
      .catch((error) => {
        this.updateValues(this.registrations, false, [...this.errors, { error }]);
        return Promise.reject();
      });
  };

  onChange: OnChange = (listener: Listener): Function => {
    const listenerFunction = this.eventEmitter.addListener(listener);
    this.#trigger();
    return listenerFunction;
  };

  /**
   * @private
   */
  #trigger: Trigger = (): void => {
    this.eventEmitter.trigger({
      registrations: this.registrations,
      isLoading: this.isLoading,
      errors: this.errors,
    });
  };
}

const instance: InterventionRegistrationService = new InterventionRegistrationService();
export default instance;
